import { Paper, Text, Group, createStyles, Box, LoadingOverlay, useMantineTheme } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useDasboardApi from '../../hooks/useDashboardApi';
import DetailMenu from './card-components/DetailMenu';
import TimeSelector from './card-components/TimeSelector';
import ReactApexChart from 'react-apexcharts';
import WeekSelector from './selectors/WeekSelector';
import FilterSelector from './selectors/FilterSelector';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: '20px 0 10px'
	},
	text: {
		fontSize: 36,
		lineHeight: '42px',
		fontWeight: 400,
		color: theme.colorScheme === 'light' ? '#4A4A4A' : '#C1C2C5',
	},
	cardTitle: {
		fontWeight: 600,
		fontSize: 16,
		lineHeight: '20px',
		color: theme.colorScheme === 'light' ? '#4A4A4A' : '#C1C2C5',
	},
	descriptionText: {
		fontSize: 12,
		lineHeight: '14px',
		fontWeight: 300,
		letterSpacing: 1.1
	}
}));

export default function UserEvolution() {

	// State and hooks
	const { classes } = useStyles();
	const [selectedRange, setSelectedRange] = useState('total');
	const [range, setRange] = useState([null, null]);
	const [visible, setVisible] = useState(false);
	const { getUserEvolution } = useDasboardApi();
	const { userEvolution, userEvolutionWeekRange, userTypes } = useSelector(state => state.dashboardData);
	const [average, setAverage] = useState(0);
	const [filters, setFilters] = useState([]);
	const theme = useMantineTheme();
	const foreColor = theme.colorScheme === 'light' ? theme.colors.dark[8] : theme.colors.gray[0];
	const [series, setSeries] = useState([{
		name: 'Total',
		data: []
	}]);
	const [options, setOptions] = useState({
		chart: {
			type: 'area',
			height: 250,
			toolbar: {
				show: false
			},
			zoom: {
				enabled: false
			},
			foreColor: foreColor,
		},
		dataLabels: {
			enabled: false
		},
		stroke: {
			curve: 'smooth',
			colors: ['#4A0382']
		},
		fill: {
			colors: ['#4A0382'],
		},
		xaxis: {
			type: 'categories',
			categories: [],
			tooltip: {
                enabled: false
            }
		},
		markers: {
			colors: theme.colors.mainPurple,
		}
	});

	// On load
	useEffect(() => {
		async function fetchData() {
			await getUserEvolution('', '', filters);
		}
		fetchData();
		// eslint-disable-next-line
	}, []);

	// Calculate average
	useEffect(() => {

		// If not null
		if (userEvolutionWeekRange) {

			// Sum from all entries
			const sum = userEvolutionWeekRange.reduce((acc, curr) => acc + curr.total, 0);
			const count = userEvolutionWeekRange.length;

			// Get and set average
			const avg = sum / count;
			setAverage(avg);

		}

	}, [userEvolutionWeekRange]);

	// Build the chart
	useEffect(() => {

		// If not null
		if (userEvolutionWeekRange) {

			// Map and format data
			const updatedSeriesData = userEvolutionWeekRange.map(item => new Intl.NumberFormat('es-AR', { notation: 'compact' }).format(item.total).replace(/\s(?=K|k|M|B|T)/, ''));
			const updatedXaxisCategories = userEvolutionWeekRange.map(item => {
				const date = new Date(item.date);
				const weekday = date.toLocaleString('es-AR', { weekday: 'short' });
				return weekday.charAt(0).toUpperCase() + weekday.slice(1);
			});

			// Set new series of data and update options
			setSeries([{ name: 'Total', data: updatedSeriesData }]);
			setOptions(prevOptions => ({
				...prevOptions,
				xaxis: { ...prevOptions.xaxis, categories: updatedXaxisCategories },
				chart: {
					foreColor: foreColor
				},
				tooltip: {
					enabled: true,
					followCursor: true,
					custom: function({ series, seriesIndex, dataPointIndex, w }) {

						// Extracting the y value and the x value (date)
						const y = series[seriesIndex][dataPointIndex];
						const x = userEvolutionWeekRange[dataPointIndex].date;

						// Format the date for the label
						const date = new Date(x).toLocaleDateString('es-AR', { year: 'numeric', month: '2-digit', day: '2-digit' });

						// Return custom HTML content for the tooltip
						return `<div style="text-align: center; padding: 5px; font-size: 12px;">${y.toFixed(0)}<br />${date}</div>`;

					}
				}
			}));

		}

	}, [userEvolutionWeekRange]);

	// Listen to selected range changes
	useEffect(() => {

		// Only if initial data has loaded
		if (userEvolution !== null) reload();

		// eslint-disable-next-line
	}, [selectedRange, filters]);

	// Reload with new values
	const reload = async () => {

		// Set loader
		setVisible(true);

		// Define date variables
		let endDate = new Date();
		let startDate = new Date();

		// Check date range to apply
		switch (selectedRange) {
			case '30d':
				startDate.setDate(endDate.getDate() - 30);
				break;
			case '7d':
				startDate.setDate(endDate.getDate() - 7);
				break;
			case 'total':
				// Remove both dates
				endDate = '';
				startDate = '';
				break;
			case 'custom':
				// Assign custom range
				endDate = range[1];
				startDate = range[0];
				break;
			default:
				// Remove both dates
				endDate = '';
				startDate = '';
				break;
		}

		// Get information
		if (selectedRange) await getUserEvolution(startDate, endDate, filters);

		// Remove loader
		setVisible(false);

	}

	return (
		<Paper
			withBorder
			p={12}
			style={{ position: 'relative' }}
			radius={18}
		>
			{/* Loading Overlay */}
			<LoadingOverlay
				visible={visible}
				overlayBlur={1}
				radius={18}
			/>
			{/* Top */}
			<Group
				position="apart"
				noWrap
			>
				{/* Title */}
				<Text className={classes.cardTitle}>
					Evolución de Registros
				</Text>
				{/* Menu */}
				<DetailMenu />
			</Group>
			{/* Time & Filter */}
			<Group
				position="apart"
				mt={8}
				noWrap
				align="center"
			>
				{/* Range */}
				<TimeSelector
					data={userEvolution}
					selectedRange={selectedRange}
					setSelectedRange={setSelectedRange}
					range={range}
					setRange={setRange}
				/>
				{/* Filter */}
				<FilterSelector
					label="Usuarios"
					options={userTypes}
					selected={[filters, setFilters]}
				/>
			</Group>
			{/* Content */}
			<Box className={classes.container}>
				{/* Container */}
				<Group
					spacing={20}
					mb={20}
				>
					{/* Number */}
					<Text className={classes.text}>
						{new Intl.NumberFormat('es-AR', { notation: 'compact' }).format(average).replace(/\s(?=K|k|M|B|T)/, '')}
					</Text>
					{/* Description */}
					<Text className={classes.descriptionText}>
						Promedio de<br />
						registros por día
					</Text>
				</Group>
				{/* Chart */}
				{userEvolution ?
					<WeekSelector />
				: null}
				<ReactApexChart
					options={options}
					series={series}
					type="area"
					height={250}
				/>
			</Box>
		</Paper>
	);

}
